import { useCallback, useState } from 'react'
import { SchemaEditorCard } from '@/components/admin/schema-playground/SchemaEditorCard'
import { ConceptsBrowser } from '@/components/schema/ConceptsBrowser'
import { FindingsPanel } from '@/components/schema/FindingsPanel'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useSchemaPlayground } from '@/features/admin/hooks/useSchemaPlayground'
import { formatJson, SAMPLE_SCHEMAS } from '@/features/admin/schema-playground-data'
import type { ValidationGate } from '@/services/api/schemas'

// Phone layout of SchemaEditorCard, set from the page because the card lives in
// components/admin. Its header keeps title + gate select + Validate on one
// ~470 px row and its sample buttons cannot wrap, which made the whole page
// scroll sideways. Below md the header wraps (title and Validate on the first
// line, the gate select full width under them, tall enough for its two-line
// value) and long sample labels wrap. From md up the wrapper is
// display:contents, so the card is laid out exactly as before.
const EDITOR_CARD_PHONE = [
  'max-md:min-w-0 md:contents',
  'max-md:[&_[data-slot=card-header]]:flex-wrap',
  'max-md:[&_[data-slot=card-header]>div:not([data-slot])]:contents',
  'max-md:[&_[data-slot=select-trigger]]:order-last max-md:[&_[data-slot=select-trigger]]:w-full',
  'max-md:[&_[data-slot=select-trigger]]:h-auto! max-md:[&_[data-slot=select-trigger]]:text-left',
  'max-md:[&_[data-slot=card-content]_button]:h-auto',
  'max-md:[&_[data-slot=card-content]_button]:max-w-full',
  'max-md:[&_[data-slot=card-content]_button]:py-1.5',
  'max-md:[&_[data-slot=card-content]_button]:text-left',
  'max-md:[&_[data-slot=card-content]_button]:whitespace-normal',
].join(' ')

export default function SchemaPlaygroundPage() {
  const [json, setJson] = useState<string>(() => formatJson(SAMPLE_SCHEMAS[0].schema))
  const [gate, setGate] = useState<ValidationGate | 'all'>('all')

  const {
    registry,
    conceptById,
    findings,
    findingsBySeverity,
    parseError,
    serverError,
    validating,
    validate,
    reset,
  } = useSchemaPlayground()

  const handleLoadSample = useCallback(
    (index: number) => {
      setJson(formatJson(SAMPLE_SCHEMAS[index].schema))
      reset()
    },
    [reset]
  )

  return (
    <div className="h-full overflow-y-auto">
      <div className="w-full space-y-6 p-4 md:p-6">
        <header className="space-y-2">
          <h1 className="text-2xl font-semibold">Schema Playground</h1>
          <p className="text-muted-foreground text-sm">
            Paste an extraction-schema JSON and run it through the concept registry. Findings come
            from the same validators that gate activation in the API; this page does not persist
            anything.
          </p>
        </header>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className={EDITOR_CARD_PHONE}>
            <SchemaEditorCard
              json={json}
              gate={gate}
              validating={validating}
              onJsonChange={setJson}
              onGateChange={setGate}
              onValidate={() => validate(json, gate)}
              onLoadSample={handleLoadSample}
            />
          </div>

          <FindingsPanel
            findings={findings}
            findingsBySeverity={findingsBySeverity}
            conceptById={conceptById}
            parseError={parseError}
            serverError={serverError}
          />
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Concept registry</CardTitle>
          </CardHeader>
          <CardContent>
            {registry === null ? (
              <div className="text-muted-foreground text-sm">Loading…</div>
            ) : (
              <ConceptsBrowser registry={registry} />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
